const STORAGE_KEY = 'tfs-size-guide-unit';
const SUGGEST_UNIT_KEY = 'tfs-size-guide-suggest-unit';

function getSavedUnit() {
  try { return localStorage.getItem(STORAGE_KEY) === 'cm' ? 'cm' : 'in'; } catch { return 'in'; }
}

function getSavedSuggestUnit() {
  try { return localStorage.getItem(SUGGEST_UNIT_KEY) === 'imperial' ? 'imperial' : 'metric'; } catch { return 'metric'; }
}

function saveValue(key, value) {
  try { localStorage.setItem(key, value); } catch {}
}

function formatMeasurement(inches, unit) {
  if (unit === 'cm') return (inches * 2.54).toFixed(1).replace(/\.0$/, '');
  return String(inches);
}

function applyUnit(root, unit) {
  if (!(root instanceof Element)) return;
  const normalized = unit === 'cm' ? 'cm' : 'in';
  root.dataset.bmpSizeActiveUnit = normalized;
  for (const button of root.querySelectorAll('[data-bmp-size-unit]')) button.setAttribute('aria-pressed', button.dataset.bmpSizeUnit === normalized ? 'true' : 'false');
  for (const cell of root.querySelectorAll('[data-inches]')) {
    const inches = Number(cell.dataset.inches);
    if (Number.isFinite(inches)) cell.textContent = formatMeasurement(inches, normalized);
  }
}

function applySuggestUnit(root, unit) {
  if (!(root instanceof Element)) return;
  const normalized = unit === 'imperial' ? 'imperial' : 'metric';
  root.dataset.bmpSuggestUnit = normalized;
  for (const button of root.querySelectorAll('[data-bmp-suggest-unit]')) button.setAttribute('aria-pressed', button.dataset.bmpSuggestUnit === normalized ? 'true' : 'false');
  for (const fields of root.querySelectorAll('[data-bmp-suggest-fields]')) fields.hidden = fields.dataset.bmpSuggestFields !== normalized;
}

function switchMode(root, mode) {
  if (!(root instanceof Element)) return;
  const normalized = mode === 'compare' ? 'compare' : 'suggest';
  for (const button of root.querySelectorAll('[data-bmp-size-mode]')) button.setAttribute('aria-selected', button.dataset.bmpSizeMode === normalized ? 'true' : 'false');
  for (const panel of root.querySelectorAll('[data-bmp-size-panel]')) panel.hidden = panel.dataset.bmpSizePanel !== normalized;
}

function selectedFit(root) {
  return root.querySelector('[data-bmp-fit][aria-pressed="true"]')?.dataset.bmpFit || 'relaxed';
}

function readMetricInputs(root) {
  return { heightCm: Number(root.querySelector('[data-bmp-height-cm]')?.value), weightKg: Number(root.querySelector('[data-bmp-weight-kg]')?.value) };
}

function readImperialInputs(root) {
  const feet = Number(root.querySelector('[data-bmp-height-ft]')?.value);
  const inches = Number(root.querySelector('[data-bmp-height-in]')?.value);
  const pounds = Number(root.querySelector('[data-bmp-weight-lb]')?.value);
  return { heightCm: (feet * 12 + inches) * 2.54, weightKg: pounds * 0.45359237 };
}

function recommendSize(heightCm, weightKg, fit) {
  const sizes = ['S', 'M', 'L', 'XL', '2XL', '3XL'];

  // Garment-specific starting bands tuned around the T-shirt's 18–28 inch
  // chest widths and 28–33 inch body lengths. Height normalizes the weight
  // signal, then tall shoppers receive a minimum length floor.
  const normalizedWeight = weightKg - (heightCm - 175) * 0.4;

  let index;
  if (normalizedWeight < 60) index = 0;
  else if (normalizedWeight < 71) index = 1;
  else if (normalizedWeight < 82) index = 2;
  else if (normalizedWeight < 95) index = 3;
  else if (normalizedWeight < 110) index = 4;
  else index = 5;

  if (heightCm >= 190) index = Math.max(index, 2);
  if (heightCm >= 198) index = Math.max(index, 3);

  if (fit === 'closer') index -= 1;
  if (fit === 'oversized') index += 1;

  return sizes[Math.max(0, Math.min(sizes.length - 1, index))];
}

function recommendationCopy(size, fit) {
  if (fit === 'closer') return 'Start with ' + size + ' for a closer fit. If you are between sizes or prefer more room, compare the garment measurements before ordering.';
  if (fit === 'oversized') return 'Start with ' + size + ' for an oversized fit. If you prefer less extra room, compare the garment measurements before ordering.';
  return 'Start with ' + size + ' for a relaxed fit. For the best check, compare the garment measurements with a T-shirt you already own.';
}

function showRecommendation(root) {
  const unit = root.dataset.bmpSuggestUnit === 'imperial' ? 'imperial' : 'metric';
  const values = unit === 'imperial' ? readImperialInputs(root) : readMetricInputs(root);
  const { heightCm, weightKg } = values;
  const error = root.querySelector('[data-bmp-size-error]');
  const result = root.querySelector('[data-bmp-size-result]');
  const valid = Number.isFinite(heightCm) && Number.isFinite(weightKg) && heightCm >= 130 && heightCm <= 220 && weightKg >= 35 && weightKg <= 200;
  if (!valid) { if (error) error.hidden = false; if (result) result.hidden = true; return; }
  if (error) error.hidden = true;
  const fit = selectedFit(root);
  const size = recommendSize(heightCm, weightKg, fit);
  const value = root.querySelector('[data-bmp-size-result-value]');
  const copy = root.querySelector('[data-bmp-size-result-copy]');
  if (value) value.textContent = size;
  if (copy) copy.textContent = recommendationCopy(size, fit);
  if (result) result.hidden = false;
}

function initialize(root = document) {
  const measurementUnit = getSavedUnit();
  const suggestUnit = getSavedSuggestUnit();
  for (const guide of root.querySelectorAll('[data-bmp-size-guide-interactive]')) {
    applyUnit(guide, measurementUnit);
    applySuggestUnit(guide, suggestUnit);
    switchMode(guide, 'suggest');
  }
}

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const guide = target.closest('[data-bmp-size-guide-interactive]');
  if (!guide) return;
  const measurementUnitButton = target.closest('[data-bmp-size-unit]');
  if (measurementUnitButton instanceof HTMLButtonElement) { const unit = measurementUnitButton.dataset.bmpSizeUnit === 'cm' ? 'cm' : 'in'; applyUnit(guide, unit); saveValue(STORAGE_KEY, unit); return; }
  const suggestUnitButton = target.closest('[data-bmp-suggest-unit]');
  if (suggestUnitButton instanceof HTMLButtonElement) { const unit = suggestUnitButton.dataset.bmpSuggestUnit === 'imperial' ? 'imperial' : 'metric'; applySuggestUnit(guide, unit); saveValue(SUGGEST_UNIT_KEY, unit); return; }
  const modeButton = target.closest('[data-bmp-size-mode]');
  if (modeButton instanceof HTMLButtonElement) { switchMode(guide, modeButton.dataset.bmpSizeMode); return; }
  const fitButton = target.closest('[data-bmp-fit]');
  if (fitButton instanceof HTMLButtonElement) { for (const button of guide.querySelectorAll('[data-bmp-fit]')) button.setAttribute('aria-pressed', button === fitButton ? 'true' : 'false'); return; }
  if (target.closest('[data-bmp-find-size]')) { showRecommendation(guide); return; }
  if (target.closest('[data-bmp-open-compare]')) switchMode(guide, 'compare');
});

document.addEventListener('dialog:open', (event) => { const target = event.target; if (target instanceof Element) initialize(target); });
document.addEventListener('shopify:section:load', (event) => { const target = event.target; if (target instanceof Element) initialize(target); });
initialize();